export interface FormGroup {
  id: number | null
  name: string | null
  isActive: boolean | null
  imageUri?: string
  prerequisiteIds: number[]
  notificationEmail: string | null
  speedType: string | null
  assignableProcessors: User[]
}

export interface Form {
  id: number | null
  formGroup: FormGroup
  name: string | null
  displayName: string | null
  description: string | null
  version: number | null
  isActive: boolean | null
  fields: FormField[]
  formType: number | null
  imageUri?: string
  costType: CostType | null
  formActions: FormAction[]
}

export interface FormField {
  id: number | null
  name: string | null
  displayName: string | null
  description: string | null
  fieldType: FieldType | null
  hoverText: string | null
  defaultValue?: string
  options: FieldOption[]
  validationRules: string | null
  render: string | null
  isPartOfCostCalculation: boolean | null
}

export enum FieldType {
  String = 0,
  Integer = 1,
  Double = 2,
  Select = 3,
  Boolean = 4,
  Email = 5,
  FileUpload = 6,
  Cost = 7,
  DisplayOnly = 8,
  DateTime = 9,
  Date = 10
}

export function fieldTypeToString(fieldType: FieldType) {
  switch (fieldType) {
    case FieldType.String:
      return 'Text'
    case FieldType.Integer:
      return 'Integer'
    case FieldType.Double:
      return 'Decimal'
    case FieldType.Select:
      return 'Select'
    case FieldType.Boolean:
      return 'True / False'
    case FieldType.Email:
      return 'Email'
    case FieldType.FileUpload:
      return 'File Upload'
    case FieldType.Cost:
      return 'Cost'
    case FieldType.DisplayOnly:
      return 'DisplayOnly'
    case FieldType.DateTime:
      return 'Date & Time'
    case FieldType.Date:
      return 'Date'
    default:
      return 'Unknown'
  }
}

export function fieldTypeToStringLiteral(fieldType: FieldType) {
  switch (fieldType) {
    case FieldType.String:
      return 'String'
    case FieldType.Integer:
      return 'Integer'
    case FieldType.Double:
      return 'Double'
    case FieldType.Select:
      return 'Select'
    case FieldType.Boolean:
      return 'Boolean'
    case FieldType.Email:
      return 'Email'
    case FieldType.FileUpload:
      return 'FileUpload'
    case FieldType.Cost:
      return 'Cost'
    case FieldType.DisplayOnly:
      return 'DisplayOnly'
    case FieldType.DateTime:
      return 'DateTime'
    case FieldType.Date:
      return 'Date'
    default:
      return 'Unknown'
  }
}

export interface FieldOption {
  optionValue: number | null
  optionName: string | null
  isDefault: boolean | null
}

import type { AdGroup } from '@/types/index'
import type { User } from './signupTypes'
import type { TransactionStatus } from './paymentTypes'

export interface FormAction {
  id?: number
  requestFormActionType: FormActionType
  adGroupGranted: AdGroup
  discountAmount: number | null
  discountGroup: string // can either be a group SID or a discount group name
  balanceChange: number | null
  emailTemplateId: string | null
  emailTemplateName: string | null
  balanceChangeAmount: number | null
}

export enum FormActionType {
  GrantAccess = 0,
  DiscountApplied = 1,
  EmailNotification = 2,
  ChangeBalance = 3
}

export enum CostType {
  Poster = 0,
  Quoted = 1,
  Assigned = 2,
  Free = 3,
  Approval = 4,
  DirectCostInput = 5,
  AssignedGranular = 6
}

export function costTypeIsCalculated(costType: CostType | null): boolean {
  return costType != null && (costType === CostType.Poster || costType === CostType.DirectCostInput)
}

export function costTypeToDescription(costType: CostType | null): string {
  if (costType === null) {
    return 'Unknown Cost Type'
  }
  if (costType === CostType.Poster) {
    return 'Poster: Cost is determined by what the users input to the fields width, height, and quantity using the formula [width / 12 * (height / 12) * 7.0 * quantity]'
  }
  if (costType === CostType.DirectCostInput) {
    return 'Direct Cost Input: Cost is determined by the user inputting a value in a field with name "cost". Ensure such a field exists, and that it\'s configured with a maximum and minimum value.'
  }
  if (costType === CostType.Assigned) {
    return 'Assigned: Cost is determined by staff once the request is reviewed and any questions are answered by the user.'
  }
  if (costType === CostType.Free) {
    return 'Free: No cost is associated with this request.'
  }
  if (costType === CostType.AssignedGranular) {
    return 'Assigned Granular: Cost is determined by staff once the request is reviewed and any questions are answered by the user. There are intermediary steps for "Ready for Fabrication" and "Ready for Pickup", plus users can mark their own requests as "Picked Up".'
  }
  return ''
}

export interface FieldValuesSubmit {
  fieldId: number
  fieldName: string
  valueAsString: string | null
  fieldType: FieldType
  fileUpload: FileUpload | null
}

export interface FileUpload {
  pathSuffix: string
  file: string | File | File[]
}

export interface SubmitFormData {
  fieldData: FormData[]
}

export interface Submission {
  id: string
  formData: SubmissionData
  customerAction?: CustomerAction
  status: SubmissionStatus
  updatedAt: string
  statusHistory: SubmissionStatusHistory[]
  transactionId: string
  transactionStatus?: TransactionStatus
  processorDescription?: string
  finalCost?: number
  quoteCost?: number
  requestedCompletion?: string
  formDataHistory?: SubmissionData[]
  assignedTo?: User
}

export interface CustomerAction {
  type: CustomerActionType
  prompt: string
  affirmativeText?: string
  negativeText?: string
  fieldsToAllowResubmissionOn?: string[]
}

export interface SubmissionStatusHistory {
  message: string
  sender: User
  senderRole: SenderRole
  createdAt: string
}

export interface SubmissionData {
  id: string
  form: Form
  submittedBy: User
  submittedData: FieldValuesSubmit[]
  createdAt: string
}

export enum SenderRole {
  Customer = 0,
  Staff = 1,
  System = 2
}

export enum SubmissionStatus {
  Unknown = 0,
  Quoted = 1,
  Submitted = 2,
  PaymentPending = 3,
  PaymentComplete = 4,
  ReadyForFabrication = 5,
  InProcess = 6,
  Delayed = 7,
  ReadyForPickup = 8,
  Completed = 9,
  FabricationActionNeeded = 10,
  CustomerActionNeeded = 11,
  Canceled = 12,
  Stale = 13,
  UnderReview = 14,
  Accepted = 15
}

export enum CustomerActionType {
  Confirmation = 0,
  ResubmitForm = 1,
  AnswerQuestion = 2
}

export enum CommunicationTemplates {
  SoftwareLicenseEmail = 'softwarelicenseemail'
}

export function submissionStatusToString(status: SubmissionStatus) {
  switch (status) {
    case SubmissionStatus.Quoted:
      return 'Quoted'
    case SubmissionStatus.Submitted:
      return 'Submitted'
    case SubmissionStatus.PaymentPending:
      return 'Payment Pending'
    case SubmissionStatus.PaymentComplete:
      return 'Payment Complete'
    case SubmissionStatus.ReadyForFabrication:
      return 'Ready for Fabrication'
    case SubmissionStatus.InProcess:
      return 'In Process'
    case SubmissionStatus.Delayed:
      return 'Delayed'
    case SubmissionStatus.ReadyForPickup:
      return 'Ready for Pickup'
    case SubmissionStatus.Completed:
      return 'Completed'
    case SubmissionStatus.FabricationActionNeeded:
      return 'Fabrication Action Needed'
    case SubmissionStatus.CustomerActionNeeded:
      return 'Customer Action Needed'
    case SubmissionStatus.Canceled:
      return 'Canceled'
    case SubmissionStatus.Stale:
      return 'Stale'
    case SubmissionStatus.UnderReview:
      return 'Under Review'
    case SubmissionStatus.Accepted:
      return 'Accepted'
    default:
      return 'Unknown'
  }
}

export var submissionStatusSelectOptions = [
  { value: null, title: 'All' },
  { value: SubmissionStatus.Unknown, title: 'In Unknown' },
  { value: SubmissionStatus.Quoted, title: 'Quoted' },
  { value: SubmissionStatus.Submitted, title: 'Submitted' },
  { value: SubmissionStatus.PaymentPending, title: 'Payment Pending' },
  { value: SubmissionStatus.PaymentComplete, title: 'Payment Complete' },
  { value: SubmissionStatus.ReadyForFabrication, title: 'Ready for Fabrication' },
  { value: SubmissionStatus.InProcess, title: 'In Process' },
  { value: SubmissionStatus.Delayed, title: 'Delayed' },
  { value: SubmissionStatus.ReadyForPickup, title: 'Ready for Pickup' },
  { value: SubmissionStatus.Completed, title: 'Completed' },
  { value: SubmissionStatus.FabricationActionNeeded, title: 'Fabrication Action Needed' },
  { value: SubmissionStatus.CustomerActionNeeded, title: 'Customer Action Needed' },
  { value: SubmissionStatus.Canceled, title: 'Canceled' },
  { value: SubmissionStatus.Stale, title: 'Stale' },
  { value: SubmissionStatus.UnderReview, title: 'Under Review' },
  { value: SubmissionStatus.Accepted, title: 'Accepted' }
]

export function statusToDisplayClass(status: SubmissionStatus) {
  switch (status) {
    case SubmissionStatus.InProcess:
      return 'blue'
    case SubmissionStatus.ReadyForFabrication:
      return 'rgb(0, 0, 139)' // darkblue
    case SubmissionStatus.Accepted:
      return 'blue'
    case SubmissionStatus.Completed:
      return 'green'
    case SubmissionStatus.ReadyForPickup:
      return 'green'
    case SubmissionStatus.Canceled:
      return 'red'
    case SubmissionStatus.CustomerActionNeeded:
      return 'orange'
    default:
      return 'grey'
  }
}

export function submissionStatusActionMap(
  statusFrom: SubmissionStatus,
  statusTo: SubmissionStatus
) {
  if (statusFrom === SubmissionStatus.InProcess && statusTo === SubmissionStatus.Completed) {
    return 'Complete'
  }
  if (statusTo === SubmissionStatus.Canceled) {
    return 'Cancel'
  }
  return 'Mark as ' + submissionStatusToString(statusTo)
}

export function parseRequestRole(role: string) {
  switch (role) {
    case '0':
      return SenderRole.Customer
    case '1':
      return SenderRole.Staff
    case '2':
      return SenderRole.System
    default:
      return SenderRole.Staff
  }
}

export interface SubmissionMessage {
  mode: MessageMode
  message: string
  resubmissionFields?: string[]
  refundIfApplicable?: boolean
}

export enum MessageMode {
  Question = 0,
  Confirmation = 1,
  Cancel = 2,
  Resubmit = 3,
  Default = 4,
  Override = 5,
  StatusUpdate = 6
}
